import type { PaginatedResponse } from '@/api/pagination'
import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/lib/query-keys'

export const ORDER_STATUSES = [
  'draft',
  'ordered',
  'awaiting_picking',
  'picked',
  'ready_for_pickup',
  'shipment_created',
  'in_transit',
  'delivered',
  'cancelled',
  'processed',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

export type FulfilmentStatus =
  | 'not_sent'
  | 'accepted'
  | 'on_hold'
  | 'awaiting_picking'
  | 'picking'
  | 'picked'
  | 'completed'
  | 'cancelled'

export type ShipmentStatus = 'created' | 'in_transit' | 'delivered' | 'failed' | 'cancelled'

export interface Order {
  id: number
  reference: string
  status: OrderStatus
  status_label: string
  fulfilment_status: FulfilmentStatus
  type: string
  created_at: string | null
  delivered_at: string | null
  organisation_name: string | null
  location_name: string | null
  shipments_count: number
  total_excluding_vat: number
  total_including_vat: number
  items_count: number
}

export interface OrderLine {
  id: number
  description: string | null
  product_name: string | null
  image_url: string | null
  amount: number
}

export interface DeliveryAddress {
  full_address: string | null
  postal_code: string | null
  city: string | null
  attention_of: string | null
}

export interface OrderShipmentItem {
  sku: string | null
  description: string | null
  quantity: number
}

export interface OrderShipment {
  reference: string | null
  status: ShipmentStatus
  status_label: string
  tracking_code: string | null
  tracking_url: string | null
  carrier: string | null
  shipped_at: string | null
  delivered_at: string | null
  items: OrderShipmentItem[]
}

export interface OrderDetail extends Order {
  delivery_address: DeliveryAddress | string | null
  shipments: OrderShipment[]
  items: OrderLine[]
}

export async function fetchOrders(params?: {
  page?: number
  search?: string
  status?: OrderStatus
  organisation_id?: number[]
}): Promise<PaginatedResponse<Order>> {
  return api.get<PaginatedResponse<Order>>('/orders', { params })
}

export async function fetchOrder(orderId: number): Promise<OrderDetail> {
  return api.get<OrderDetail>(`/orders/${orderId}`)
}

export function ordersQueryOptions(params?: {
  page?: number
  search?: string
  status?: OrderStatus
  organisation_id?: number[]
}) {
  return queryOptions({
    queryKey: queryKeys.orders.list(params),
    queryFn: async () => fetchOrders(params),
  })
}

export function orderQueryOptions(orderId: number) {
  return queryOptions({
    queryKey: queryKeys.orders.detail(orderId),
    queryFn: async () => fetchOrder(orderId),
  })
}
